import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_OBJ } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  AutomationStatus,
  IExperience,
  IExperienceTemplate,
  MessageStatus,
  PARTICIPANT_ACTOR_KEY,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { IUser } from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import { Button } from '@material-ui/core';
import { compact, flatten, uniq } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matchesExclusiveAllFilter } from '../../../../../components/ChipFilterWithDropdown';
import { Loader } from '../../../../../components/Loader';
import { usePromise } from '../../../../../hooks/usePromise';
import { useUnsafeTranslation } from '../../../../../i18next';
import { Centered } from '../../../../../layout/Centered';
import { useStringQueryParam } from '../../../../../routes';
import {
  AutomationStats,
  EMPTY_AUTOMATION_STATS,
  getAutomationsStats,
  getInstancesThatHaveThisAutomation
} from '../../../../../services/experiences/automation';
import { useExperienceInstancesCurrentlyRelevant } from '../../../../../services/experiences/instance';
import {
  usePeopleTabV2Enabled,
  useTimelineItemPseudoInstances
} from '../../../../../services/experiences/timelineItemStatus';
import {
  getInstancesThatHaveThisMessage,
  getFullMessageStatusIncludingMostRecentConcreteMessageStatus,
  getStatsForMessage,
  MessageStats
} from '../../../../../services/experiences/message';
import {
  EMPTY_TASK_STATS,
  getInstancesThatHaveThisTask,
  getTaskStats,
  TaskStats
} from '../../../../../services/experiences/task';
import {
  getAutomationTemplateById,
  getMessageTemplateById,
  getTaskTemplateById
} from '../../../../../services/experiences/template';
import { useProductAnalytics } from '../../../../../services/productAnalytics';
import {
  getUsersAsMap,
  matchUserBySearchTerm,
  toSearchTermRegexp
} from '../../../../../services/user';
import { ExecutionReportHeader } from '../../../components/ExecutionReportHeader';
import { AutomationsExecutionReportTable } from '../../../components/ExecutionReportTable/Automations';
import { MessagesExecutionReportTable } from '../../../components/ExecutionReportTable/Messages';
import { TasksExecutionReportTable } from '../../../components/ExecutionReportTable/Tasks';
import { ParticipantDialog, ParticipantDialogLazy } from '../ParticipantDialog';

export type SearchFilter = {
  name: string;
  status: string[];
};

const LayoutContainer = styled('div')((p) => ({
  display: 'flex',
  flex: 1,
  gap: 4,
  flexDirection: 'column',
  padding: p.theme.spacing(2)
}));

const HeaderContainer = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center',
  padding: p.theme.spacing(1)
}));

const BodyContainer = styled('div')<{ newUx?: boolean }>((p) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  // `scroll` always reserves the scrollbar gutter (a phantom right margin even
  // when nothing overflows); NEW_UX uses `auto` so the track only appears when
  // needed. Classic keeps `scroll` unchanged.
  overflowY: p.newUx ? 'auto' : 'scroll'
}));

const EmptyTable = styled('div')((p) => ({
  display: 'flex',
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: p.theme.custom.colors.gray.w100,
  color: p.theme.custom.colors.gray.w500
}));

const taskStatusStat = (task: TaskStats) => {
  return [
    {
      status: TaskStatus.COMPLETE,
      count: task.totalDone
    },
    {
      status: TaskStatus.NOT_STARTED,
      count: task.notStarted
    },
    {
      status: TaskStatus.LATE,
      count: task.inProgressLate
    },
    {
      status: TaskStatus.RUNNING,
      count: task.totalInProgress - task.inProgressLate
    },
    {
      status: TaskStatus.SKIPPED,
      count: task.skipped
    }
  ];
};

const messageStatusStat = (msg: MessageStats) => {
  return [
    {
      status: 'OK',
      count: msg.done
    },
    {
      status: MessageStatus.UPCOMING,
      count: msg.upcoming
    },
    {
      status: MessageStatus.ERROR,
      count: msg.error
    },
    {
      status: MessageStatus.SKIPPED,
      count: msg.skipped
    }
  ];
};

const automationStatusStat = (automation: AutomationStats) => {
  return [
    {
      status: AutomationStatus.DONE,
      count: automation.done
    },
    {
      status: AutomationStatus.UPCOMING,
      count: automation.upcoming
    },
    {
      status: AutomationStatus.ERROR,
      count: automation.error
    },
    {
      status: AutomationStatus.SKIPPED,
      count: automation.skipped
    }
  ];
};

const INITIAL_SEARCH_FILTERS: {
  AUTOMATIONS: Partial<SearchFilter>;
  MESSAGES: Partial<SearchFilter>;
  TASKS: Partial<SearchFilter>;
} = {
  AUTOMATIONS: {
    status: ['DONE', 'ERROR', 'UPCOMING', 'SKIPPED']
  },
  MESSAGES: {
    status: ['OK', 'ERROR', 'UPCOMING', 'SKIPPED']
  },
  TASKS: {
    // 'DONE' is not a TaskStatus (tasks COMPLETE, not DONE) — it was a dead
    // entry, and PAST_DEADLINE was missing, so past-deadline task rows were
    // filtered out of the tab entirely. Use the real terminal status.
    status: [
      'PAST_DEADLINE',
      'COMPLETE',
      'LATE',
      'RUNNING',
      'SKIPPED',
      'NOT_STARTED'
    ]
  }
};

const useSearchFilter = (
  initialFilter: Partial<SearchFilter> = EMPTY_OBJ
): [SearchFilter, (filterNew: Partial<SearchFilter>) => any, () => any] => {
  const initialFilterWithDefaults = useMemo<SearchFilter>(() => {
    return {
      name: '',
      status: [],
      ...initialFilter
    };
  }, [initialFilter]);

  const [filter, setFilter] = useState<SearchFilter>(initialFilterWithDefaults);
  return useMemo(() => {
    return [
      filter,
      (filterNew) =>
        setFilter({
          ...filter,
          ...filterNew
        }),
      () => setFilter(initialFilterWithDefaults)
    ];
  }, [filter, setFilter, initialFilterWithDefaults]);
};

const useUserMap = (instances: Doc<IExperience>[]) =>
  usePromise(async () => {
    // compact shouldn't be needed here - for some reason an undefined
    // userId crept into our sample content -> this gets rid of it.
    const userIds = compact(
      uniq(flatten(instances.map((instance) => instance.data.userIds)))
    );
    return getUsersAsMap(userIds);
  }, [instances]);

const getFilteredInstances = (
  instances: Doc<IExperience>[],
  users: { [userId: string]: Doc<IUser> | null },
  searchFilter: SearchFilter,
  getStatus: (instance: Doc<IExperience>) => string | null,
  // Classic's StatusSelector chips are literal per-status toggles defaulting to
  // all-on, so an empty status array there genuinely means "everything deselected,
  // show nothing". NEW_UX's status dropdown instead uses ChipFilterWithDropdown's
  // exclusiveAll "All" meta-option, whose canonical empty-array representation means
  // "no filter applied, show everything" — see matchesExclusiveAllFilter. Branch here
  // rather than changing the shared predicate's meaning for classic.
  useExclusiveAllSemantics = false
) => {
  const searchRe = searchFilter.name
    ? toSearchTermRegexp(searchFilter.name)
    : null;

  return instances.filter((instance) => {
    const status = getStatus(instance);

    return (
      status &&
      (useExclusiveAllSemantics
        ? matchesExclusiveAllFilter(searchFilter.status, status)
        : searchFilter.status.includes(status)) &&
      (!searchRe ||
        instance.data.userIds.some((uid) => {
          const user = users[uid];
          return user && matchUserBySearchTerm(searchRe, user);
        }))
    );
  });
};

const EmptyState = ({ onResetFilters }: { onResetFilters: () => any }) => {
  const { t } = useUnsafeTranslation();

  return (
    <Centered>
      {t('No results for these filters.')}
      <Button variant="text" onClick={() => onResetFilters()}>
        {t('Reset filters')}
      </Button>
    </Centered>
  );
};

const NoDataState = ({ text }: { text?: string }) => {
  const { t } = useUnsafeTranslation();
  return (
    <LayoutContainer>
      <EmptyTable>{text ? t(text) : t('No Data To Show Here')}</EmptyTable>
    </LayoutContainer>
  );
};

const TaskExecutionReportTabInner = ({
  instances,
  taskTplId,
  taskTplTitle,
  tpl,
  newUx
}: {
  instances: Doc<IExperience>[];
  taskTplId: string;
  taskTplTitle: string;
  tpl: Doc<IExperienceTemplate>;
  newUx?: boolean;
}) => {
  const { t } = useTranslation();
  const { trackEvent } = useProductAnalytics();
  const task = getTaskTemplateById(tpl, taskTplId);
  const data = useMemo(
    () => ({
      instances: getInstancesThatHaveThisTask(instances, taskTplId),
      taskStats: getTaskStats(instances)
    }),
    [instances, taskTplId]
  );
  const stats = taskStatusStat(data.taskStats[taskTplId] || EMPTY_TASK_STATS());

  const [searchFilter, setSearchFilter, resetSearchFilter] = useSearchFilter(
    newUx ? undefined : INITIAL_SEARCH_FILTERS.TASKS
  );

  const [users, isLoading] = useUserMap(data.instances);

  const [dialogInstanceId, setDialogInstanceId] = useStringQueryParam('i', '');
  const setDialogInstance = useCallback(
    (inst: Doc<IExperience>) => setDialogInstanceId(inst.id),
    [setDialogInstanceId]
  );
  // Under PEOPLE_TAB_V2 the list holds compact projection pseudo-instances, which
  // the single-instance dialog cannot render (needs full timing/audience/content).
  // Re-fetch the full instance via ParticipantDialogLazy, matching Participants.tsx.
  const peopleTabV2Enabled = usePeopleTabV2Enabled();

  useEffect(() => {
    if (searchFilter.name !== '') {
      trackEvent({
        eventType: PAEventType.EXPERIENCE_TPL,
        name: 'Timeline Item Modal People Tab Searched',
        Explanation: 'Admin user searches in the People Tab',
        'Timeline Item ID': taskTplId,
        'Experience Template ID': tpl.id,
        'Timeline Item Title': taskTplTitle,
        'Timeline Item Type': 'TASK',
        'Number of Results': filteredInstances.length
      });
    }
  }, [searchFilter.name]);

  if (isLoading && !users) {
    return <Loader />;
  }

  if (!task || !users) {
    return null;
  }

  const dialogInstance =
    dialogInstanceId && data.instances
      ? data.instances.find((inst) => inst.id === dialogInstanceId) || null
      : null;

  const filteredInstances: Doc<IExperience>[] = getFilteredInstances(
    data.instances.filter((inst) =>
      inst.data.tasks.some(
        (t) => t.templateId === taskTplId && t.actor === task.actor
      )
    ),
    users,
    searchFilter,
    (instance) =>
      instance.data.tasks.find((t) => t.templateId === taskTplId)?.status ||
      null,
    newUx
  );

  if (!data.instances.length) {
    return <NoDataState />;
  }

  return (
    <>
      <LayoutContainer>
        <HeaderContainer>
          <ExecutionReportHeader
            filter={searchFilter}
            stats={stats}
            onNameChange={(name) => setSearchFilter({ name })}
            onStatusChange={(status) => setSearchFilter({ status })}
            experienceTemplateId={tpl.id}
            elementId={taskTplId}
            type={'TASK'}
            title={taskTplTitle}
            initialSearchFilter={INITIAL_SEARCH_FILTERS.TASKS.status}
            newUx={newUx}
          />
        </HeaderContainer>

        <BodyContainer newUx={newUx}>
          {filteredInstances.length ? (
            <TasksExecutionReportTable
              instances={filteredInstances}
              taskTplId={taskTplId}
              taskTplTitle={taskTplTitle}
              tpl={tpl}
              onShowInstanceDetails={setDialogInstance}
              taskTpl={task}
              actor={
                task.actor === PARTICIPANT_ACTOR_KEY
                  ? {
                      key: PARTICIPANT_ACTOR_KEY,
                      label: t('Participant')
                    }
                  : {
                      key: task.actor,
                      label: tpl.data.supportingActors[task.actor].name
                    }
              }
            />
          ) : (
            <EmptyState onResetFilters={resetSearchFilter} />
          )}
        </BodyContainer>
      </LayoutContainer>
      {peopleTabV2Enabled
        ? dialogInstanceId && (
            <ParticipantDialogLazy
              instanceId={dialogInstanceId}
              tplId={tpl.id}
              open={true}
              onClose={() => setDialogInstanceId('')}
            />
          )
        : dialogInstance && (
            <ParticipantDialog
              open={true}
              tpl={tpl}
              d={dialogInstance}
              onClose={() => setDialogInstanceId('')}
            />
          )}
    </>
  );
};

type TaskTabProps = {
  tpl: Doc<IExperienceTemplate>;
  taskTplId: string;
  taskTplTitle: string;
  /** NEW_UX: render the Status dropdown filter + kit search (task viewer). */
  newUx?: boolean;
};

const TaskExecutionReportTabLoaded = ({
  instances,
  ...rest
}: TaskTabProps & { instances: Doc<IExperience>[] | void }) => {
  if (!instances) {
    return <Loader />;
  }
  if (!instances.length) {
    return <NoDataState text="No participants to show" />;
  }
  return <TaskExecutionReportTabInner instances={instances} {...rest} />;
};

const TaskExecutionReportTabV1 = (props: TaskTabProps) => {
  const [instances] = useExperienceInstancesCurrentlyRelevant(
    props.tpl.data.orgId,
    props.tpl.id,
    props.tpl.data.expectedEndThreshold
  );
  return <TaskExecutionReportTabLoaded instances={instances} {...props} />;
};

const TaskExecutionReportTabV2 = (props: TaskTabProps) => {
  const [instances] = useTimelineItemPseudoInstances(
    props.tpl.data.orgId,
    props.tpl.id,
    props.tpl.data.expectedEndThreshold
  );
  return <TaskExecutionReportTabLoaded instances={instances} {...props} />;
};

// Self-loading + flag-branched (consistent with Automation/Message tabs), so the
// task read-only dialog no longer threads full instances from the Details page.
export const TaskExecutionReportTab = (props: TaskTabProps) =>
  usePeopleTabV2Enabled() ? (
    <TaskExecutionReportTabV2 {...props} />
  ) : (
    <TaskExecutionReportTabV1 {...props} />
  );

const AutomationExecutionReportTabInner = ({
  autoTplId,
  autoTplTitle,
  instances,
  tpl,
  newUx
}: {
  autoTplId: string;
  autoTplTitle: string;
  instances: Doc<IExperience>[];
  tpl: Doc<IExperienceTemplate>;
  newUx?: boolean;
}) => {
  const { t } = useTranslation();
  const { trackEvent } = useProductAnalytics();
  const automation = getAutomationTemplateById(tpl, autoTplId);
  const data = useMemo(
    () => ({
      instances: getInstancesThatHaveThisAutomation(instances, autoTplId),
      automationStats: getAutomationsStats(instances)
    }),
    [autoTplId, instances]
  );
  const stats = automationStatusStat(
    data.automationStats[autoTplId] || EMPTY_AUTOMATION_STATS()
  );

  const [searchFilter, setSearchFilter, resetSearchFilter] = useSearchFilter(
    newUx ? undefined : INITIAL_SEARCH_FILTERS.AUTOMATIONS
  );

  const [users, isLoading] = useUserMap(data.instances);

  const [dialogInstanceId, setDialogInstanceId] = useStringQueryParam('i', '');
  const setDialogInstance = useCallback(
    (inst: Doc<IExperience>) => setDialogInstanceId(inst.id),
    [setDialogInstanceId]
  );
  // Under PEOPLE_TAB_V2 the list holds compact projection pseudo-instances, which
  // the single-instance dialog cannot render (needs full timing/audience/content).
  // Re-fetch the full instance via ParticipantDialogLazy, matching Participants.tsx.
  const peopleTabV2Enabled = usePeopleTabV2Enabled();

  useEffect(() => {
    if (searchFilter.name !== '') {
      trackEvent({
        eventType: PAEventType.EXPERIENCE_TPL,
        name: 'Timeline Item Modal People Tab Searched',
        Explanation: 'Admin user searches in the People Tab',
        'Timeline Item ID': autoTplId,
        'Experience Template ID': tpl.id,
        'Timeline Item Title': autoTplTitle,
        'Timeline Item Type': 'AUTOMATION',
        'Number of Results': filteredInstances.length
      });
    }
  }, [searchFilter.name]);

  if (isLoading) {
    return <Loader />;
  }

  if (!automation || !users) {
    return null;
  }

  const dialogInstance =
    dialogInstanceId && data.instances
      ? data.instances.find((inst) => inst.id === dialogInstanceId) || null
      : null;

  const filteredInstances: Doc<IExperience>[] = getFilteredInstances(
    data.instances,
    users,
    searchFilter,
    (instance) => instance.data.automations[autoTplId]?.status,
    newUx
  );

  if (!data.instances.length) {
    return <NoDataState />;
  }

  return (
    <>
      <LayoutContainer>
        <HeaderContainer>
          <ExecutionReportHeader
            filter={searchFilter}
            stats={stats}
            onNameChange={(name) => setSearchFilter({ name })}
            onStatusChange={(status) => setSearchFilter({ status })}
            experienceTemplateId={tpl.id}
            elementId={autoTplId}
            type={'AUTOMATION'}
            title={autoTplTitle}
            initialSearchFilter={INITIAL_SEARCH_FILTERS.AUTOMATIONS.status}
            newUx={newUx}
          />
        </HeaderContainer>

        <BodyContainer newUx={newUx}>
          {filteredInstances.length ? (
            <AutomationsExecutionReportTable
              instances={filteredInstances}
              autoTplId={autoTplId}
              autoTplTitle={autoTplTitle}
              tpl={tpl}
              onShowInstanceDetails={setDialogInstance}
              actor={{
                key: PARTICIPANT_ACTOR_KEY,
                label: t('Participant')
              }}
            />
          ) : (
            <EmptyState onResetFilters={resetSearchFilter} />
          )}
        </BodyContainer>
      </LayoutContainer>
      {peopleTabV2Enabled
        ? dialogInstanceId && (
            <ParticipantDialogLazy
              instanceId={dialogInstanceId}
              tplId={tpl.id}
              open={true}
              onClose={() => setDialogInstanceId('')}
            />
          )
        : dialogInstance && (
            <ParticipantDialog
              open={true}
              tpl={tpl}
              d={dialogInstance}
              onClose={() => setDialogInstanceId('')}
            />
          )}
    </>
  );
};

type AutomationTabProps = {
  tpl: Doc<IExperienceTemplate>;
  autoTplId: string;
  autoTplTitle: string;
  /** NEW_UX: render the Status dropdown filter + kit search (automation viewer). */
  newUx?: boolean;
};

const AutomationExecutionReportTabLoaded = ({
  instances,
  ...rest
}: AutomationTabProps & { instances: Doc<IExperience>[] | void }) => {
  if (!instances) {
    return <Loader />;
  }
  if (!instances.length) {
    return <NoDataState text="No participants to show" />;
  }
  return <AutomationExecutionReportTabInner instances={instances} {...rest} />;
};

// V1: full experiencesV1 instances (current path).
const AutomationExecutionReportTabV1 = (props: AutomationTabProps) => {
  const [instances] = useExperienceInstancesCurrentlyRelevant(
    props.tpl.data.orgId,
    props.tpl.id,
    props.tpl.data.expectedEndThreshold
  );
  return <AutomationExecutionReportTabLoaded instances={instances} {...props} />;
};

// V2 (PEOPLE_TAB_V2): compact timelineItemStatusV1 projections as pseudo-instances.
const AutomationExecutionReportTabV2 = (props: AutomationTabProps) => {
  const [instances] = useTimelineItemPseudoInstances(
    props.tpl.data.orgId,
    props.tpl.id,
    props.tpl.data.expectedEndThreshold
  );
  return <AutomationExecutionReportTabLoaded instances={instances} {...props} />;
};

// Branch at the COMPONENT level so exactly one data source subscribes (rules of
// hooks + no double-load).
export const AutomationExecutionReportTab = (props: AutomationTabProps) =>
  usePeopleTabV2Enabled() ? (
    <AutomationExecutionReportTabV2 {...props} />
  ) : (
    <AutomationExecutionReportTabV1 {...props} />
  );

const MessageExecutionReportTabInner = ({
  tpl,
  instances,
  msgId,
  msgTitle,
  newUx
}: {
  tpl: Doc<IExperienceTemplate>;
  instances: Doc<IExperience>[];
  msgId: string;
  msgTitle: string;
  newUx?: boolean;
}) => {
  const { t } = useTranslation();
  const { trackEvent } = useProductAnalytics();
  const message = getMessageTemplateById(tpl, msgId);
  const data = useMemo(
    () => ({
      instances: getInstancesThatHaveThisMessage(instances, msgId),
      stats: getStatsForMessage(instances, msgId)
    }),
    [instances, msgId]
  );
  const stats = messageStatusStat(data.stats);

  const [searchFilter, setSearchFilter, resetSearchFilter] = useSearchFilter(
    newUx ? undefined : INITIAL_SEARCH_FILTERS.MESSAGES
  );

  const [users, isLoading] = useUserMap(data.instances);

  const [dialogInstanceId, setDialogInstanceId] = useStringQueryParam('i', '');
  const setDialogInstance = useCallback(
    (inst: Doc<IExperience>) => setDialogInstanceId(inst.id),
    [setDialogInstanceId]
  );
  // Under PEOPLE_TAB_V2 the list holds compact projection pseudo-instances, which
  // the single-instance dialog cannot render (needs full timing/audience/content).
  // Re-fetch the full instance via ParticipantDialogLazy, matching Participants.tsx.
  const peopleTabV2Enabled = usePeopleTabV2Enabled();

  useEffect(() => {
    if (searchFilter.name !== '') {
      trackEvent({
        eventType: PAEventType.EXPERIENCE_TPL,
        name: 'Timeline Item Modal People Tab Searched',
        Explanation: 'Admin user searches in the People Tab',
        'Timeline Item ID': msgId,
        'Experience Template ID': tpl.id,
        'Timeline Item Title': msgTitle,
        'Timeline Item Type': 'MESSAGE',
        'Number of Results': filteredInstances.length
      });
    }
  }, [searchFilter.name]);

  if (isLoading) {
    return <Loader />;
  }

  if (!message || !users) {
    return null;
  }

  const dialogInstance =
    dialogInstanceId && data.instances
      ? data.instances.find((inst) => inst.id === dialogInstanceId) || null
      : null;

  const filteredInstances: Doc<IExperience>[] = getFilteredInstances(
    data.instances,
    users,
    searchFilter,
    // Skip-aware status so SKIPPED (and DONE→'OK') messages appear in the People
    // tab. getMostRecentSuccessfulConcreteMessageOrAnyOther returns null for a
    // SKIPPED message (no concrete send), which getFilteredInstances dropped —
    // this helper returns 'SKIPPED'/'UPCOMING'/'OK'/'ERROR', matching the allow-list
    // + the stat chips (which already use it).
    (instance) =>
      getFullMessageStatusIncludingMostRecentConcreteMessageStatus(instance, msgId),
    newUx
  );

  if (!data.instances.length) {
    return <NoDataState />;
  }

  return (
    <>
      <LayoutContainer>
        <HeaderContainer>
          <ExecutionReportHeader
            filter={searchFilter}
            stats={stats}
            onNameChange={(name) => setSearchFilter({ name })}
            onStatusChange={(status) => setSearchFilter({ status })}
            experienceTemplateId={tpl.id}
            elementId={msgId}
            type={'MESSAGE'}
            title={msgTitle}
            initialSearchFilter={INITIAL_SEARCH_FILTERS.MESSAGES.status}
            newUx={newUx}
          />
        </HeaderContainer>

        <BodyContainer newUx={newUx}>
          {filteredInstances.length ? (
            <MessagesExecutionReportTable
              instances={filteredInstances}
              msgId={msgId}
              msgTitle={msgTitle}
              onShowInstanceDetails={setDialogInstance}
              tpl={tpl}
              actor={
                message.actor === PARTICIPANT_ACTOR_KEY
                  ? {
                      key: PARTICIPANT_ACTOR_KEY,
                      label: t('Participant')
                    }
                  : {
                      key: message.actor,
                      label: tpl.data.supportingActors[message.actor].name
                    }
              }
            />
          ) : (
            <EmptyState onResetFilters={resetSearchFilter} />
          )}
        </BodyContainer>
      </LayoutContainer>
      {peopleTabV2Enabled
        ? dialogInstanceId && (
            <ParticipantDialogLazy
              instanceId={dialogInstanceId}
              tplId={tpl.id}
              open={true}
              onClose={() => setDialogInstanceId('')}
            />
          )
        : dialogInstance && (
            <ParticipantDialog
              open={true}
              tpl={tpl}
              d={dialogInstance}
              onClose={() => setDialogInstanceId('')}
            />
          )}
    </>
  );
};

type MessageTabProps = {
  tpl: Doc<IExperienceTemplate>;
  msgId: string;
  msgTitle: string;
  /** NEW_UX: render the Status dropdown filter + kit search (message viewer). */
  newUx?: boolean;
};

const MessageExecutionReportTabLoaded = ({
  instances,
  ...rest
}: MessageTabProps & { instances: Doc<IExperience>[] | void }) => {
  if (!instances) {
    return <Loader />;
  }
  if (!instances.length) {
    return <NoDataState text="No participants to show" />;
  }
  return <MessageExecutionReportTabInner instances={instances} {...rest} />;
};

const MessageExecutionReportTabV1 = (props: MessageTabProps) => {
  const [instances] = useExperienceInstancesCurrentlyRelevant(
    props.tpl.data.orgId,
    props.tpl.id,
    props.tpl.data.expectedEndThreshold
  );
  return <MessageExecutionReportTabLoaded instances={instances} {...props} />;
};

const MessageExecutionReportTabV2 = (props: MessageTabProps) => {
  const [instances] = useTimelineItemPseudoInstances(
    props.tpl.data.orgId,
    props.tpl.id,
    props.tpl.data.expectedEndThreshold
  );
  return <MessageExecutionReportTabLoaded instances={instances} {...props} />;
};

export const MessageExecutionReportTab = (props: MessageTabProps) =>
  usePeopleTabV2Enabled() ? (
    <MessageExecutionReportTabV2 {...props} />
  ) : (
    <MessageExecutionReportTabV1 {...props} />
  );
